import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import DoNotDisturbAltRounded from "@mui/icons-material/DoNotDisturbAltRounded";
import EventRepeatRounded from "@mui/icons-material/EventRepeatRounded";
import { Box, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE } from "../../theme/tokens";
import type { BulkNurseJobsAction, BulkNurseJobsBlockedMode, BulkNurseJobsPreview, Job } from "../../utils/models";
import { type FlatSwitchOption, SystemFlatSwitch } from "../UIToolkit/Clickable/SystemFlatSwitch";
import { NamedGroupCard, type NamedGroupSection } from "../UIToolkit/molecules/NamedGroupCard";
import { BulkJobChangeHero } from "./BulkJobChangeHero";
import { countWillChange, toNamedItems } from "./bulkJobChange";
import { countSkipped } from "./useBulkNurseJobs";

type Props = {
	action: BulkNurseJobsAction;
	fromJob: Job | undefined;
	toJob: Job;
	preview: BulkNurseJobsPreview;
	nameById: Map<number, string>;
	blockedMode: BulkNurseJobsBlockedMode;
	onBlockedModeChange: (mode: BulkNurseJobsBlockedMode) => void;
};

/** Step 2: exactly who changes, who has shifts in the way (and what to do with them), and who is skipped. */
export const BulkJobReviewStep: FC<Props> = ({ action, fromJob, toJob, preview, nameById, blockedMode, onBlockedModeChange }) => {
	const { t } = useTranslation();
	const showNames = (count: number) => t("team.bulk.jobs.list.show", { count });
	const hideNames = t("team.bulk.jobs.list.hide");

	const willChangeSections = useMemo<NamedGroupSection[]>(
		() => [{ id: "all", items: toNamedItems(preview.willChange, nameById) }],
		[preview.willChange, nameById],
	);
	const blockedSections = useMemo<NamedGroupSection[]>(
		() => [{ id: "all", items: toNamedItems(preview.blockedByShifts, nameById) }],
		[preview.blockedByShifts, nameById],
	);
	const skippedSections = useSkippedSections(preview, nameById, fromJob, toJob);
	const skippedCount = countSkipped(preview.skipped);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
			<BulkJobChangeHero action={action} fromJob={fromJob} toJob={toJob} willChangeCount={countWillChange(preview, blockedMode)} />
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
				{preview.willChange.length > 0 && (
					<NamedGroupCard
						title={t("team.bulk.jobs.group.will_change")}
						count={preview.willChange.length}
						tone="primary"
						icon={<CheckCircleOutlineRounded />}
						sections={willChangeSections}
						showLabel={showNames}
						hideLabel={hideNames}
						dataTestId="team-bulk-jobs-group-will-change"
					/>
				)}
				{preview.blockedByShifts.length > 0 && fromJob && (
					<NamedGroupCard
						title={t("team.bulk.jobs.group.blocked", { job: fromJob.displayName })}
						count={preview.blockedByShifts.length}
						tone="warning"
						icon={<EventRepeatRounded />}
						description={t("team.bulk.jobs.group.blocked.description", { job: fromJob.displayName })}
						sections={blockedSections}
						showLabel={showNames}
						hideLabel={hideNames}
						dataTestId="team-bulk-jobs-group-blocked"
					>
						<BlockedChoice fromJob={fromJob} toJob={toJob} blockedMode={blockedMode} onBlockedModeChange={onBlockedModeChange} />
					</NamedGroupCard>
				)}
				{skippedCount > 0 && (
					<NamedGroupCard
						title={t("team.bulk.jobs.group.skipped")}
						count={skippedCount}
						tone="neutral"
						icon={<DoNotDisturbAltRounded />}
						sections={skippedSections}
						showLabel={showNames}
						hideLabel={hideNames}
						dataTestId="team-bulk-jobs-group-skipped"
					/>
				)}
			</Box>
		</Box>
	);
};

/** One labelled run per skip reason, in the order an admin can act on them. */
const useSkippedSections = (preview: BulkNurseJobsPreview, nameById: Map<number, string>, fromJob: Job | undefined, toJob: Job): NamedGroupSection[] => {
	const { t } = useTranslation();
	const { skipped } = preview;
	return useMemo(
		() => [
			{
				id: "already-has-job",
				label: t("team.bulk.jobs.skip.already_has_job", { job: toJob.displayName }),
				items: toNamedItems(skipped.alreadyHasJob, nameById),
			},
			{
				id: "does-not-hold-job",
				label: t("team.bulk.jobs.skip.does_not_hold_job", { job: fromJob?.displayName }),
				items: toNamedItems(skipped.doesNotHoldJob, nameById),
			},
			{ id: "not-home-facility", label: t("team.bulk.jobs.skip.not_home_facility"), items: toNamedItems(skipped.notHomeFacility, nameById) },
			{ id: "not-in-department", label: t("team.bulk.jobs.skip.not_in_department"), items: toNamedItems(skipped.notInDepartment, nameById) },
		],
		[t, skipped, nameById, fromJob, toJob],
	);
};

type BlockedChoiceProps = {
	fromJob: Job;
	toJob: Job;
	blockedMode: BulkNurseJobsBlockedMode;
	onBlockedModeChange: (mode: BulkNurseJobsBlockedMode) => void;
};

/** The single-employee "Apply shifts to new job" decision, made once for everyone it applies to. */
const BlockedChoice: FC<BlockedChoiceProps> = ({ fromJob, toJob, blockedMode, onBlockedModeChange }) => {
	const { t } = useTranslation();
	const options = useMemo<FlatSwitchOption<BulkNurseJobsBlockedMode>[]>(
		() => [
			{ label: t("team.bulk.jobs.blocked.move", { job: toJob.displayName }), value: "move_shifts", "data-testid": "team-bulk-jobs-blocked-move" },
			{ label: t("team.bulk.jobs.blocked.leave"), value: "leave_unchanged", "data-testid": "team-bulk-jobs-blocked-leave" },
		],
		[t, toJob.displayName],
	);
	const detail =
		blockedMode === "move_shifts"
			? t("team.bulk.jobs.blocked.move.detail", { from: fromJob.displayName, to: toJob.displayName })
			: t("team.bulk.jobs.blocked.leave.detail", { from: fromJob.displayName });

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start", pt: 0.5 }}>
			<SystemFlatSwitch<BulkNurseJobsBlockedMode>
				data-testid="team-bulk-jobs-blocked-mode"
				options={options}
				activeIndexOrValue={blockedMode}
				onSwitch={(option) => onBlockedModeChange(option.value)}
				delayCallback={0}
				shape="rectangular"
				equalWidthOptions
				rootWidth="100%"
				fontSizeOverrides={{ xs: FONT_SIZE.SECONDARY, sm: FONT_SIZE.SECONDARY, lg: FONT_SIZE.SECONDARY, xl: FONT_SIZE.SECONDARY }}
			/>
			<Typography
				role="status"
				aria-live="polite"
				variant="body2"
				sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.primary" }}
				data-testid="team-bulk-jobs-blocked-detail"
			>
				{detail}
			</Typography>
		</Box>
	);
};
